import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { API, Storage } from 'aws-amplify'
import { v4 as uuidv4 } from 'uuid'
import { MEDICAL_RECORDS_BUCKET } from 'consts/appConsts'
import { logError } from 'utils/logUtils'

export const BriefAutofillState = {
  Idle: 'idle',
  Analyzing: 'analyzing',
  Done: 'done',
  NothingFilled: 'nothingFilled',
  Failed: 'failed',
  NotAPlan: 'notAPlan'
}

// Bedrock Converse caps each document at 4.5 MB
export const MAX_TREATMENT_PLAN_BYTES = 4.5 * 1024 * 1024
// Amplify Storage sets no upload timeout of its own
export const UPLOAD_TIMEOUT_MS = 20000
// Just under API Gateway's 29s cut-off, so the request is cancelled instead of left hanging
export const REQUEST_TIMEOUT_MS = 28500
const EXTRACT_PATH = '/copilot/v1/hi/patient-brief/ai-extract'

// Failures a second attempt can fix; the zone says "try again" for these
export const RETRYABLE_FAILURE_REASONS = [
  'model_timeout',
  'model_unavailable',
  'storage_timeout',
  'upload_timeout',
  'upload_error',
  'request_timeout',
  'request_error'
]

const STATUS_TO_STATE = {
  ok: BriefAutofillState.Done,
  not_a_plan: BriefAutofillState.NotAPlan,
  failed: BriefAutofillState.Failed
}

class AutofillTimeoutError extends Error {
  constructor(stage) {
    super(`Brief autofill ${stage} timed out`)
    this.name = 'AutofillTimeoutError'
  }
}

const withTimeout = (promise, timeoutMs, { stage, onTimeout }) => {
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      onTimeout()
      reject(new AutofillTimeoutError(stage))
    }, timeoutMs)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

const cancelQuietly = cancel => {
  try {
    cancel()
  } catch (error) {
    // Cancelling is best effort; the timeout already settled the analysis
  }
}

// Amplify API errors are axios errors (response.status); S3 SDK v3 errors from Storage.put carry $metadata
const errorHttpStatus = error => error?.response?.status ?? error?.$metadata?.httpStatusCode

const clientFailureReason = (stage, error) => {
  const httpStatus = errorHttpStatus(error)
  if (error instanceof AutofillTimeoutError || httpStatus === 504) {
    return `${stage}_timeout`
  }
  if (httpStatus === 401 || httpStatus === 403) {
    return 'forbidden'
  }
  // Other 4xx won't change on retry; 408 and 429 will
  if (httpStatus >= 400 && httpStatus < 500 && httpStatus !== 408 && httpStatus !== 429) {
    return `${stage}_rejected`
  }
  return `${stage}_error`
}

const useBriefAutofill = ({ patientId, isOpen, onResult }) => {
  const [state, setState] = useState(BriefAutofillState.Idle)
  const [failureReason, setFailureReason] = useState(null)
  // The outcome of the last analysis, attached to the reviewer's feedback; null when there is none
  const [lastResult, setLastResult] = useState(null)
  // Bumped on every new upload and on open/close/patient change, so late results are dropped
  const requestIdRef = useRef(0)
  const onResultRef = useRef(onResult)
  onResultRef.current = onResult

  // A layout effect, so an analysis that settles between this commit and the passive effects below
  // is already stale and never reaches the next patient's onResult
  useLayoutEffect(() => {
    requestIdRef.current += 1
  }, [isOpen, patientId])

  useEffect(() => {
    setState(BriefAutofillState.Idle)
    setFailureReason(null)
    setLastResult(null)
  }, [isOpen, patientId])

  const analyzeFile = useCallback(
    async file => {
      const requestId = ++requestIdRef.current
      setState(BriefAutofillState.Analyzing)
      setFailureReason(null)
      setLastResult(null)
      const startedAt = Date.now()

      let response
      let stage = 'upload'
      try {
        const s3Key = `patient-brief-plans/${patientId}/${uuidv4()}.pdf`
        const upload = Storage.put(s3Key, file, { bucket: MEDICAL_RECORDS_BUCKET, contentType: 'application/pdf' })
        await withTimeout(upload, UPLOAD_TIMEOUT_MS, {
          stage,
          onTimeout: () => cancelQuietly(() => Storage.cancel(upload))
        })
        stage = 'request'
        const request = API.post('grinServerlessApi', EXTRACT_PATH, { body: { patientId, s3Key } })
        response = await withTimeout(request, REQUEST_TIMEOUT_MS, {
          stage,
          onTimeout: () => cancelQuietly(() => API.cancel(request))
        })
      } catch (error) {
        const httpStatus = errorHttpStatus(error)
        // Only name, message and status: the axios error's config carries the id token
        logError('Brief autofill failed', {
          patientId,
          stage,
          errorName: error?.name,
          errorMessage: error?.message,
          httpStatus
        })
        response = {
          status: 'failed',
          analysisId: null,
          failureReason: clientFailureReason(stage, error),
          failureStage: stage,
          httpStatus
        }
      }

      if (requestIdRef.current !== requestId) {
        return
      }
      const status = STATUS_TO_STATE[response?.status] ? response.status : 'failed'
      const result = {
        status,
        fields: response?.fields,
        analysisId: response?.analysisId || null,
        latencyMs: Date.now() - startedAt,
        failureReason: status === 'failed' ? response?.failureReason || 'unknown' : undefined,
        failureStage: status === 'failed' ? response?.failureStage || 'analysis' : undefined,
        httpStatus: response?.httpStatus
      }

      let appliedFields
      try {
        appliedFields = onResultRef.current?.(result)
      } catch (error) {
        logError('Brief autofill apply failed', { patientId, errorName: error?.name, errorMessage: error?.message })
        setState(BriefAutofillState.Failed)
        setFailureReason('apply_error')
        setLastResult({
          status: 'failed',
          analysisId: result.analysisId,
          failureReason: 'apply_error',
          filledFields: []
        })
        return
      }

      const nothingFilled = status === 'ok' && Array.isArray(appliedFields) && appliedFields.length === 0
      setState(nothingFilled ? BriefAutofillState.NothingFilled : STATUS_TO_STATE[status])
      setFailureReason(result.failureReason || null)
      setLastResult({
        status,
        analysisId: result.analysisId,
        failureReason: result.failureReason,
        filledFields: Array.isArray(appliedFields) ? appliedFields : []
      })
    },
    [patientId]
  )

  const rejectFile = useCallback(reason => {
    requestIdRef.current += 1
    setState(BriefAutofillState.Failed)
    setFailureReason(reason)
    setLastResult(null)
  }, [])

  return { state, failureReason, lastResult, analyzeFile, rejectFile }
}

export default useBriefAutofill
